import { useBaseInfoStore, useDoubleContractAssetsAccountStore, useExchangeInfoStore, useQuoteStore } from 'store'
import { ContractCalculation } from 'utils'
import { UiUtils } from '..'
import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractServices } from 'network'

export const useUpdateMargin = (position: ApiResponse.DoubleContractPosition) => {
  const updateMarginVisible = ref(false)
  const exchangeStore = useExchangeInfoStore()
  const baseInfoStore = useBaseInfoStore()
  const quoteStore = useQuoteStore()
  const addSide = ref(true)
  const amount = ref('')
  const loading = ref(false)
  const doubleContractAssetsAccountStore = useDoubleContractAssetsAccountStore()
  const { t } = useI18n()

  const open = () => {
    updateMarginVisible.value = true
  }
  const close = () => {
    updateMarginVisible.value = false
  }

  const symbolInfo = computed(() => getSymbolInfo())

  const getSymbolInfo = () => {
    return exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
  }

  const markPrice = computed(() => {
    const quote = quoteStore.getSymbolBySymbolIdWithContractList(position.symbolId)
    return quote?.markPrice
  })

  const getSymbolIcon = () => {
    const symbolInfo = getSymbolInfo()
    if (!symbolInfo?.baseItemId) return ''
    const config = baseInfoStore.getCoinInfoByItemId(symbolInfo!.baseItemId)
    return config?.itemDetail.coinUrl
  }

  const switchMarginType = () => {
    addSide.value = !addSide.value
  }

  /** 可用保证金 */
  const availableMargin = computed(() => {
    const asset = doubleContractAssetsAccountStore.assets.find((item) => item.itemId === 10007)
    if (position.marginType === 1) {
      return asset?.availableBalance || 0
    }
    return asset?.crossAvailableBalance || 0
  })

  const canReduce = computed(() => {
    // 当前保证金-初始保证金+未实现盈亏
    let _canReduce = position.margin.subtractStr(position.initMargin).addStr(position.unrlzPnl).cutDecimal(2)
    if (_canReduce.isGreaterThanOrEqualTo(position.margin)) {
      _canReduce = position.margin.subtractStr(0.01)
    }
    if (_canReduce.isLessThanOrEqualTo(0)) {
      _canReduce = '0'
    }
    return _canReduce
  })

  const onUpdateMargin = async () => {
    if (!amount.value || amount.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      throw UiUtils.showError(t('请输入金额'))
    }
    if (addSide.value) {
      if (amount.value.isGreaterThan(availableMargin.value)) {
        throw UiUtils.showError(t('可用资产不足'))
      }
    } else {
      if (amount.value.isGreaterThan(canReduce.value)) {
        throw UiUtils.showError(t('不能大于可减少金额'))
      }
    }
    const res = await ContractServices.doubleContractUpdateMargin({
      symbolId: position.symbolId,
      amount: amount.value,
      type: addSide.value ? 1 : -1,
      positionType: position.direction,
    })
    if (res.status === ApiStatus.Success) {
      UiUtils.showSuccess(t('调整保证金成功'))
      doubleContractAssetsAccountStore.refresh()
    }
  }

  const onAll = () => {
    if (addSide.value) {
      amount.value = availableMargin.value.toString()
    } else {
      amount.value = canReduce.value.toString()
    }
  }

  const newMargin = computed(() => {
    if (!amount.value) {
      return position.margin
    }
    const ballAfter = addSide.value ? position.margin.addStr(amount.value) : position.margin.subtractStr(amount.value)

    return ballAfter || 0
  })

  const newLiquidationPrice = computed(() => {
    // 追加后本金
    let ballAfter = newMargin.value

    if (ballAfter.isLessThanOrEqualTo(0)) {
      ballAfter = 0
    }

    const config = exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)

    const result = ContractCalculation.getLiquidatePriceOfPosition(
      {
        symbolId: position.symbolId,
        margin: ballAfter,
        holePrice: position.holdPrice,
        qty: position.qty,
        contractValue: position.contractValue,
        side: position.direction,
        pricePrecision: config?.pricePrecision,
        takerFee: position.takerFeeRate,
      },
      config,
    )
    return result
  })

  return {
    updateMarginVisible,
    open,
    close,
    getSymbolIcon,
    getSymbolInfo,
    symbolInfo,
    markPrice,
    switchMarginType,
    addSide,
    amount,
    loading,
    onUpdateMargin,
    availableMargin,
    onAll,
    newMargin,
    newLiquidationPrice,
    canReduce,
  }
}
